import React from "react";
import { useDispatch } from "./redux/store";
import { updateAppointment } from "./redux/slices/appointmentSlice";
import { AppointmentState } from "./types";
import {
  getNextStage,
  getStage4DisabledReasons,
  isNextEnabledForStage,
  isStage2SelectionNextEnabled,
} from "./utils/stageValidation";
import {
  ACTIVE_NAVIGATION_STAGES,
  BookingStage,
} from "./constants/bookingStages";

interface FooterProps {
  appointment: AppointmentState;
  headerColor: string;
  stageOnePlusFooterTextColor: string;
  stageOnePlusFooterBorderColor: string;
  stageOnePlusFooterBackground: string;
}

const Footer: React.FC<FooterProps> = ({
  appointment,
  headerColor,
  stageOnePlusFooterTextColor,
  stageOnePlusFooterBorderColor,
  stageOnePlusFooterBackground,
}) => {
  const dispatch = useDispatch();

  const privacyPolicyUrl = appointment?.shopData?.privacyPolicy;

  if (appointment?.stage === BookingStage.Location) {
    return (
      <div
        className="z-10 px-3 sm:px-5 flex justify-center items-center text-[14px] !font-roboto !font-normal mt-0 py-2 md:py-4 min-h-[48px] md:min-h-[64px]"
        style={{ backgroundColor: headerColor, color: "#6b6b6b" }}
      >
        <div className="mx-auto flex items-center gap-2">
          <span className="text-sm font-normal">Powered by</span>
          <img
            src="/images/OktoRocket-2025-LandscapeLogo-RGB.png"
            alt="OktoRocket"
            className="h-6 sm:h-8 w-auto align-middle"
          />
        </div>
      </div>
    );
  }

  if (
    ACTIVE_NAVIGATION_STAGES.includes((appointment?.stage ?? -1) as BookingStage)
  ) {
    const isAppointmentOptionsStage =
      appointment?.stage === BookingStage.AppointmentOptions;
    const isAppointmentOptionsCommentsStep =
      isAppointmentOptionsStage &&
      appointment?.appointmentOptionsStep === "comments";
    const isNextEnabled =
      isAppointmentOptionsStage && !isAppointmentOptionsCommentsStep
        ? isStage2SelectionNextEnabled(appointment)
        : isNextEnabledForStage(appointment?.stage, appointment);

    const isConfirmStage = appointment?.stage === BookingStage.VehicleInfo;
    const shouldPulseAdvanceButton = isNextEnabled;

    return (
      <div
        className="z-10 px-3 sm:px-5 grid grid-cols-3 items-center text-[14px] !font-roboto !font-normal pt-2 md:pt-3 min-h-[48px] md:min-h-[64px] border-t border-gray-200"
        style={{
          backgroundColor: stageOnePlusFooterBackground,
          borderTopColor: stageOnePlusFooterBorderColor,
          paddingBottom: privacyPolicyUrl ? "4px" : undefined,
        }}
      >
        <div className="justify-self-start">
          {(appointment?.entryStage === BookingStage.Location &&
            (appointment?.stage ?? BookingStage.Location) > BookingStage.Location) ||
          (appointment?.entryStage === BookingStage.ServiceSelection &&
            (appointment?.stage ?? BookingStage.Location) >
              BookingStage.ServiceSelection) ? (
            <button
              type="button"
              className="flex items-center gap-2 cursor-pointer rounded-lg border-2 border-solid px-4 py-2 text-sm font-semibold transition-opacity hover:opacity-80 active:opacity-70 focus:outline-none"
              style={{
                backgroundColor: "#ffffff",
                borderColor: appointment?.shopData?.color || "#2b73b9",
                color: appointment?.shopData?.color || "#2b73b9",
                boxShadow: "none",
              }}
              onClick={() =>
                dispatch(
                  updateAppointment({
                    updates:
                      isAppointmentOptionsCommentsStep
                        ? { appointmentOptionsStep: "selection" }
                        : {
                            stage:
                              (appointment?.stage ??
                                BookingStage.ServiceSelection) - 1,
                          },
                  })
                )
              }
              data-testid="back-button"
            >
              <i
                className="fa-solid fa-chevron-left"
                aria-hidden="true"
                style={{ fontSize: "14px", lineHeight: 1 }}
              />
              <span>Back</span>
            </button>
          ) : (
            <div />
          )}
        </div>

        <div
          className="justify-self-center flex items-center gap-2"
          style={{ color: stageOnePlusFooterTextColor }}
        >
          <span className="whitespace-nowrap text-[11px] text-[#9ca3af] md:text-sm md:text-inherit font-normal">Powered by</span>
          <img
            src="/images/OktoRocket-2025-LandscapeLogo-RGB.png"
            alt="OktoRocket"
            className="h-4 md:h-8 w-auto align-middle"
          />
        </div>

        <div
          className="justify-self-end"
          onMouseEnter={() => {
            if (isConfirmStage && !isNextEnabled) {
              const reasons = getStage4DisabledReasons(appointment);
              console.log(
                "[Confirm disabled] reasons:",
                reasons.length
                  ? reasons
                  : "(no reasons returned — check isStage4ConfirmEnabled)"
              );
            }
          }}
        >
          <button
            type="button"
            id="booking-next-button"
            className={`flex items-center gap-2 rounded-lg px-5 py-2 text-sm font-semibold transition-opacity focus:outline-none ${
              isNextEnabled
                ? "cursor-pointer hover:opacity-90 active:opacity-80"
                : "cursor-not-allowed opacity-50"
            } ${shouldPulseAdvanceButton ? "confirm-radiate-button" : ""}`}
            style={
              {
                backgroundColor: appointment?.shopData?.color || "#2b73b9",
                color: "#ffffff",
                border: "none",
                boxShadow: "none",
                "--confirm-radiate-color": appointment?.shopData?.color || "#2b73b9",
              } as React.CSSProperties
            }
            onClick={() => {
              if (isNextEnabled) {
                if (
                  isAppointmentOptionsStage &&
                  !isAppointmentOptionsCommentsStep
                ) {
                  dispatch(
                    updateAppointment({
                      updates: { appointmentOptionsStep: "comments" },
                    })
                  );
                  return;
                }

                const nextStage = getNextStage(appointment);
                dispatch(
                  updateAppointment({
                    updates: {
                      stage: nextStage,
                    },
                  })
                );
              }
            }}
            disabled={!isNextEnabled}
            data-testid={isConfirmStage ? "booking-confirmation" : "next-button"}
          >
            <span>{isConfirmStage ? "Confirm" : "Next"}</span>
            {!isConfirmStage && (
              <i
                className="fa-solid fa-chevron-right"
                aria-hidden="true"
                style={{ fontSize: "14px", lineHeight: 1 }}
              />
            )}
          </button>
        </div>

        {privacyPolicyUrl && (
          <div className="col-span-3 text-center pt-1 pb-1">
            <a
              href={privacyPolicyUrl}
              target="_blank"
              rel="noreferrer"
              className="text-[11px] hover:underline"
              style={{ color: stageOnePlusFooterTextColor }}
            >
              Terms & Conditions and Privacy Policy
            </a>
          </div>
        )}
      </div>
    );
  }

  // Stages 5, 6, 7 and any other stages: centered "Powered by" footer
  return (
    <div
      className="z-10 px-3 sm:px-5 flex flex-col items-center text-[14px] !font-roboto !font-normal pt-2 md:pt-3 min-h-[48px] md:min-h-[64px] border-t border-gray-200"
      style={{
        backgroundColor: stageOnePlusFooterBackground,
        borderTopColor: stageOnePlusFooterBorderColor,
        paddingBottom: privacyPolicyUrl ? "4px" : undefined,
      }}
    >
      <div
        className="flex items-center gap-2"
        style={{ color: stageOnePlusFooterTextColor }}
      >
        <span className="text-sm font-normal">Powered by</span>
        <img
          src="/images/OktoRocket-2025-LandscapeLogo-RGB.png"
          alt="OktoRocket"
          className="h-9 sm:h-8 w-auto align-middle"
        />
      </div>
      {privacyPolicyUrl && (
        <a
          href={privacyPolicyUrl}
          target="_blank"
          rel="noreferrer"
          className="text-[11px] hover:underline pt-1 pb-1"
          style={{ color: stageOnePlusFooterTextColor }}
        >
          Terms & Conditions and Privacy Policy
        </a>
      )}
    </div>
  );
};

export default Footer;
